import { Eye, EyeSlash, Info } from '@phosphor-icons/react'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { client } from '../api/client'
import { LanguageSwitcher } from '../components/LanguageSwitcher'
import { useAuthStore } from '../stores/auth'

interface PhoneData {
  number: string
  phone_type: string
}

interface RegisterForm {
  first_name: string
  last_name: string
  email: string
  phones: PhoneData[]
  password: string
  password_confirm: string
}

export function RegisterPage() {
  const { t } = useTranslation(['auth', 'profile'])
  const navigate = useNavigate()
  const { setToken, setParent } = useAuthStore()
  const [error, setError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [phones, setPhones] = useState<PhoneData[]>([{ number: '', phone_type: 'mobile' }])

  const { register, handleSubmit, formState: { errors }, watch } = useForm<RegisterForm>()
  const password = watch('password', '') // Watch the password field for validation

  const addPhone = () => {
    if (phones.length < 5) {
      setPhones([...phones, { number: '', phone_type: 'home' }])
    } else {
      setError(t('validation.maxPhones', { ns: 'profile' }))
    }
  }

  const removePhone = (index: number) => {
    if (phones.length > 1) {
      setPhones(phones.filter((_, i) => i !== index))
    }
  }

  const updatePhoneType = (index: number, type: string) => {
    const newPhones = [...phones]
    newPhones[index].phone_type = type
    setPhones(newPhones)
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const onSubmit = async (data: RegisterForm) => {
    try {
      // Reset errors
      setError('')
      setEmailError('')
      setPasswordError('')
      
      // Filter out empty phone numbers
      const validPhones = phones.filter(phone => phone.number.trim() !== '')
      
      // Check if at least one phone number exists
      if (validPhones.length === 0) {
        setError(t('register.phoneNumberRequired'))
        return
      }

      // Check if at least one mobile phone exists
      const hasMobilePhone = validPhones.some(phone => phone.phone_type === 'mobile')
      if (!hasMobilePhone) {
        setError(t('register.mobileRequired'))
        return
      }
      
      // Check if there are more than 5 phones
      if (validPhones.length > 5) {
        setError(t('validation.maxPhones', { ns: 'profile' }))
        return
      }
      
      // Create the API request data
      const apiData = {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phones: validPhones,
        password: data.password,
        password_confirm: data.password_confirm,
        language_preference: localStorage.getItem('i18nextLng') || 'el' // Get current language preference
      }
      
      const response = await client.post('/auth/register/', apiData)
      // Save the token and parent data in auth store
      const { token, parent } = response.data
      setToken(token)
      setParent(parent)
      navigate('/verification-pending')
    } catch (err: any) {
      // Check for specific email error
      if (err.response?.data?.email && 
          Array.isArray(err.response.data.email) && 
          err.response.data.email.some((msg: string) => msg.includes('Parent with this Email Address already exists'))) {
        setEmailError(t('register.emailAlreadyExists'))
      } 
      // Check for password errors
      else if (err.response?.data?.password) {
        // Check for common password error
        if (Array.isArray(err.response.data.password) && 
            err.response.data.password.some((msg: string) => msg.includes('This password is too common'))) {
          setPasswordError(t('register.passwordTooCommon'))
        } else {
          // Set the appropriate error message based on the backend response
          if (Array.isArray(err.response.data.password)) {
            setError(err.response.data.password[0])
          } else {
            setError(err.response.data.password)
          }
        }
      }
      // Check for password_confirm errors
      else if (err.response?.data?.password_confirm) {
        // Set the appropriate error message based on the backend response
        if (Array.isArray(err.response.data.password_confirm)) {
          setError(err.response.data.password_confirm[0])
        } else {
          setError(err.response.data.password_confirm)
        }
      }
      // Check for non-field errors
      else if (err.response?.data?.non_field_errors) {
        if (Array.isArray(err.response.data.non_field_errors)) {
          setError(err.response.data.non_field_errors[0])
        } else {
          setError(err.response.data.non_field_errors)
        }
      }
      else {
        setError(err.response?.data?.error || t('register.error'))
      }
    }
  }

  return (
    <>
      {/* Fixed Language Switcher */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>

      <div className="auth-container" style={{ minHeight: '100vh' }}>
        <div className="max-w-md w-full">
          {/* Standalone Logo */}
          <div className="flex justify-center mb-8">
            <img src="/images/logo.png" alt="Logo" />
          </div>

          <div className="card-neobrutalism mb-8">
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
              {error && error !== t('register.phoneNumberRequired') && 
                       error !== t('register.mobileRequired') && 
                       error !== t('validation.maxPhones', { ns: 'profile' }) && (
                <div className="bg-red-100 border-2 border-black p-4">
                  <div className="text-sm font-bold text-red-900">{error}</div>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label htmlFor="first-name" className="block text-sm font-bold text-neobrutalism-text mb-2">
                    {t('register.firstName')}
                    <span className="text-gray-500 text-sm italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
                  </label>
                  <input
                    id="first-name"
                    {...register('first_name', { required: t('register.firstNameRequired') })}
                    type="text"
                    className="input-neobrutalism w-full"
                  />
                  {errors.first_name && (
                    <p className="mt-1 text-sm font-bold text-red-600">{errors.first_name.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="last-name" className="block text-sm font-bold text-neobrutalism-text mb-2">
                    {t('register.lastName')}
                    <span className="text-gray-500 text-sm italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
                  </label>
                  <input
                    id="last-name"
                    {...register('last_name', { required: t('register.lastNameRequired') })}
                    type="text"
                    className="input-neobrutalism w-full"
                  />
                  {errors.last_name && (
                    <p className="mt-1 text-sm font-bold text-red-600">{errors.last_name.message}</p>
                  )}
                  {/* Name requirements hint */}
                  <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600 flex items-start gap-2">
                    <Info size={16} className="mt-0.5 flex-shrink-0" />
                    <span>{t('register.nameRequirements')}</span>
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-neobrutalism-text mb-2">
                    {t('register.email')}
                    <span className="text-gray-500 text-sm italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
                  </label>
                  <input
                    id="email"
                    {...register('email', { 
                      required: t('register.emailRequired'),
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: t('register.invalidEmail')
                      }
                    })}
                    type="email"
                    className="input-neobrutalism w-full"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm font-bold text-red-600">{errors.email.message}</p>
                  )}
                  {emailError && (
                    <p className="mt-1 text-sm font-bold text-red-600">{emailError}</p>
                  )}
                </div>

                {/* Phone Numbers Section */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <label className="block text-sm font-bold text-neobrutalism-text">
                      {t('register.phoneNumbers')}
                      <span className="text-gray-500 text-sm italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
                    </label>
                    <button
                      type="button"
                      onClick={addPhone}
                      disabled={phones.length >= 5}
                      className={`text-sm font-bold ${phones.length >= 5 ? 'text-gray-400 cursor-not-allowed' : 'text-neobrutalism-primary hover:text-neobrutalism-primary-dark'}`}
                    >
                      + {t('register.addPhone')}
                    </button>
                  </div>
                  
                  {phones.map((phone, index) => (
                    <div key={index} className="flex gap-2">
                      <select
                        value={phone.phone_type}
                        onChange={(e) => updatePhoneType(index, e.target.value)}
                        className="input-neobrutalism w-1/3"
                      >
                        <option value="mobile">{t('register.mobile')}</option>
                        <option value="home">{t('register.home')}</option>
                        <option value="work">{t('register.work')}</option>
                        <option value="other">{t('register.other')}</option>
                      </select>
                      <input
                        type="tel"
                        value={phone.number}
                        onChange={(e) => {
                          const newPhones = [...phones]
                          newPhones[index].number = e.target.value
                          setPhones(newPhones)
                        }}
                        className="input-neobrutalism w-2/3"
                        placeholder={t('register.phoneNumberPlaceholder')}
                      />
                      {phones.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removePhone(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  
                  {/* Phone-related error messages - moved to bottom with updated styling */}
                  {error && (error === t('register.phoneNumberRequired') || 
                             error === t('register.mobileRequired') || 
                             error === t('validation.maxPhones', { ns: 'profile' })) && (
                    <p className="mt-1 text-sm font-bold text-red-600">{error}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-bold text-neobrutalism-text mb-2">
                    {t('register.password')}
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      {...register('password', { 
                        required: t('register.passwordRequired'),
                        minLength: {
                          value: 8,
                          message: t('register.passwordMinLength')
                        },
                        pattern: {
                          // Pattern to check for at least one letter and one number
                          value: /^(?=.*[A-Za-z])(?=.*\d).+$/,
                          message: t('register.passwordComplexity')
                        }
                      })}
                      type={showPassword ? "text" : "password"}
                      className="input-neobrutalism w-full pr-10"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      onClick={togglePasswordVisibility}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm font-bold text-red-600">{errors.password.message}</p>
                  )}
                  {passwordError && (
                    <p className="mt-1 text-sm font-bold text-red-600">{passwordError}</p>
                  )}
                  {/* Password requirements hint */}
                  <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600">
                    <ul className="list-disc pl-4 space-y-1">
                      <li>{t('register.passwordMinLength')}</li>
                      <li>{t('register.passwordComplexity')}</li>
                      <li>{t('register.passwordNotCommon')}</li>
                      <li>{t('register.passwordLatinOnly')}</li>
                      <li>{t('register.passwordNoEmail')}</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <label htmlFor="password-confirm" className="block text-sm font-bold text-neobrutalism-text mb-2">
                    {t('register.confirmPassword')}
                  </label>
                  <input
                    id="password-confirm"
                    {...register('password_confirm', { 
                      required: t('register.confirmPasswordRequired'),
                      validate: value => 
                        value === password || t('register.passwordsDoNotMatch')
                    })}
                    type="password"
                    className="input-neobrutalism w-full"
                  />
                  {errors.password_confirm && (
                    <p className="mt-1 text-sm font-bold text-red-600">{errors.password_confirm.message}</p>
                  )}
                </div>
              </div>

              <button 
                type="submit" 
                className="btn-neobrutalism w-full text-xs sm:text-base py-2 sm:py-3 px-2 sm:px-4"
              >
                {t('register.createAccount')}
              </button>
            </form>
          </div>

          <div className="card-neobrutalism">
            <p className="text-center text-lg">
              {t('register.hasAccount')}{' '}
              <Link to="/login" className="link-neobrutalism">
                {t('register.loginLink')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </>
  )
} 